/**
 * Event helpers for the internal client.
 *
 * The generic stream-parsing logic (dedupe, payload parsing — and the
 * richer chat reducers) lives in `@runloop/reflex-ui`, the published chat
 * component library; it is the source of truth and is re-exported here so
 * web and plugins keep their import paths. Only helpers that depend on
 * internal concerns (the Axon viewer's wire shape) stay in this file.
 */
export { deduplicateEvents, parseEventPayload } from '@runloop/reflex-ui';

/** Payload pretty-printer for the raw-payload toggle. */
export function formatPayloadRaw(payload: unknown): { text: string; isJson: boolean } {
  if (payload == null) return { text: '—', isJson: false };
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      return { text: JSON.stringify(parsed, null, 2), isJson: true };
    } catch {
      return { text: payload, isJson: false };
    }
  }
  if (typeof payload === 'object') {
    return { text: JSON.stringify(payload, null, 2), isJson: true };
  }
  return { text: String(payload), isJson: false };
}

/**
 * Reconstruct the raw Axon API event shape (`AxonEventView`) from the
 * internally-transformed `AxonEvent`. Field names match the Axon API
 * (`axon_id`, `event_type`, `timestamp_ms`, `sequence`) so the Axon
 * viewer displays events as they exist on the wire. The `payload` is
 * kept as a parsed object for readability rather than re-stringified.
 */
export function buildFullMessage(event: {
  id: string;
  sequence?: number;
  streamId: string;
  type: string;
  payload: unknown;
  timestamp: number;
  origin?: string;
  source?: string;
}): Record<string, unknown> {
  const sequence =
    event.sequence ??
    (() => {
      const parts = event.id.split(':');
      const n = Number(parts[parts.length - 1]);
      return Number.isFinite(n) ? n : undefined;
    })();

  return {
    ...(sequence != null && { sequence }),
    axon_id: event.streamId,
    event_type: event.type,
    payload: event.payload,
    timestamp_ms: event.timestamp,
    ...(event.origin && { origin: event.origin }),
    ...(event.source && { source: event.source }),
  };
}
